/**
 * QuickLaunchComponent handles the initial step of the auto-login flow:
 * 1. **Quick Launch:** Validates an authentication token using a cloud function.
 *    - If valid, it stores the token and triggers auto-login.
 *    - If invalid or missing, redirects to partial registration.
 * 2. **Partial Registration:** Handles incomplete user accounts or missing details.
 * 3. **Final Destination:** Grants access to the main app if authentication succeeds.
 */

import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { LoadingService } from "@core/loading.service";
import { TimeoutManagerService } from '@core/timeout-manager.service';
import { finalize, ReplaySubject, takeUntil, retry, timer, throwError, mergeMap } from 'rxjs';
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { Network } from "@capacitor/network";
import { NetworkService } from '@core/network.service';
import { PrivateBrowsingDetectorService } from "@core/private-browsing-detector.service";
import { canonicalizeProsubQueryParams, resolveProsubId } from "@core/prosub-query-params";
@Component({
  selector: "app-quick-launch",
  templateUrl: "./quick-launch.component.html",
  styleUrls: ["./quick-launch.component.scss"],
  standalone: true,
  imports: [RouterLink],
  providers: [TimeoutManagerService],
})

export class QuickLaunchComponent implements OnInit {
  private readonly timeoutDuration = 5000;
  private readonly defaultRedirect = '/partial-registration';
  private params = { ...this.route.snapshot.queryParams };
  private destroyCallAuth$ = new ReplaySubject<void>(1);
  private active = true;
  loginFailed = false;

  timeoutCheck;
  constructor(
    private router: Router,
    private loadingService: LoadingService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private GTMSegment: GTMSegmentService,
    private timeoutManager: TimeoutManagerService,
    private networkService: NetworkService,
    private privateBrowsingDetector: PrivateBrowsingDetectorService
  ) { }

  ngOnInit() {
    this.checkCustomLogin();
  }

  private extractToken(params: AuthParams): string | null {
    return params.t || params.T || null;
  }

  private extractTP1(params: AuthParams): string | null {
    return (params.tp1 as string) || (params.TP1 as string) || null;
  }

  private extractTP2(params: AuthParams): string | null {
    return (params.tp2 as string) || (params.TP2 as string) || null;
  }

  private extractProsub(params: AuthParams): string | null {
    return resolveProsubId(params) || null;
  }

  private handleRedirect() {
    if (!this.active) return;
    this.timeoutManager.clearTimeout();
    this.router.navigate([this.defaultRedirect], { queryParams: this.params });
  }
  async checkCustomLogin() {
    // Performance tracking
    const startTime = performance.now();
    if (!this.active) return;
    this.params = canonicalizeProsubQueryParams({ ...this.params });
    this.loadingService.present();
    this.timeoutManager.startTimeout(() => this.showLoginRecovery(), this.timeoutDuration);

    try {
      const networkStatus = await Network.getStatus();
      if (!this.active) return;
      if (!networkStatus.connected) {
        this.timeoutManager.clearTimeout();
        this.loadingService.dismiss();
        this.router.navigate(['/offline-waiting'], { queryParams: this.params });
        return;
      }

      const token = this.extractToken(this.params);
      const tp1 = this.extractTP1(this.params);
      const tp2 = this.extractTP2(this.params);
      const prosubId = this.extractProsub(this.params);
      if (token) {
        localStorage.setItem("user_auto_login_token", token);
      }

      if (!token) {
        this.loadingService.dismiss();
        this.handleRedirect();
        return;
      }

      const isPrivateBrowsing = await this.privateBrowsingDetector.detectPrivateBrowsing();
      if (!this.active) return;
      if (isPrivateBrowsing) {
        console.warn('Private browsing mode detected in quick-launch. Proceeding with limited functionality.');
      }

      // Clear user session
      await this.authService.clearUserSession();
      if (!this.active) return;
      
      // Monitor network status during auth process
      this.networkService.isOnline().pipe(
        takeUntil(this.destroyCallAuth$)
      ).subscribe(online => {
        if (!online) {
          this.active = false;
          this.timeoutManager.clearTimeout();
          this.destroyCallAuth$.next();
          this.loadingService.dismiss();
          this.router.navigate(['/offline-waiting'], { queryParams: this.params });
        }
      });
      if (!this.active) return;
      
      // Make cloud function call with optimized parameters and retry logic
      this.authService
        .getV2CloudFunction("validateautologinv2", { 
          login_token: token, 
          prosub_id: prosubId, 
          tp1: tp1, 
          tp2: tp2 
        })
        .pipe(
          // Retry logic for transient Firestore SDK "offline" errors
          retry({
            count: 3,
            delay: (error, retryCount) => {
              // Check if this is a transient Firestore offline error
              const isFirestoreOfflineError = this.isTransientFirestoreError(error);
              
              if (!isFirestoreOfflineError) {
                // Don't retry for actual errors, throw asynchronously to avoid
                // synchronous call stack recursion (RangeError: Maximum call stack size exceeded)
                return timer(0).pipe(mergeMap(() => throwError(() => error)));
              }
              
              // Log retry attempt
              console.warn(`Retrying validateautologinv2 (attempt ${retryCount}/3) due to transient Firestore offline error`);
              
              // Exponential backoff: 500ms, 1000ms, 2000ms
              const delayMs = Math.min(500 * Math.pow(2, retryCount - 1), 2000);
              return timer(delayMs);
            }
          }),
          takeUntil(this.destroyCallAuth$),
          finalize(() => {
            // Log performance metrics
            const endTime = performance.now();
            console.info(`Quick Launch Loading Time: ${endTime - startTime}ms`);

          })
        )
        .subscribe({
          next: (res) => {
            if (!this.active) return;
            // Handle offline state
            if (res?.offline) {
              this.timeoutManager.clearTimeout();
              this.loadingService.dismiss();
              // Failure tracking: offline response during auto-login
              this.GTMSegment.collectVisitorData("", "", 1, 0);
              this.router.navigate(['/offline-waiting'], { queryParams: this.params });
              return;
            }
            const { status, token } = res || {};
            if (status === "success" && token) {
              localStorage.setItem("fbToken", token);
              this.toastMessage("1154");
              // Keep the timeout active through token validation and Firebase sign-in;
              // clear it only after sign-in succeeds so recovery cannot race navigation.
              this.authService.autoLogin(
                token,
                this.params,
                () => this.active,
                this.destroyCallAuth$,
                () => this.timeoutManager.clearTimeout(),
              ).catch(() => this.showLoginRecovery());
              // Success tracking: auto-login succeeded
              this.GTMSegment.collectVisitorData("", "", 1, 1);
            } else {
              this.timeoutManager.clearTimeout();
              this.loadingService.dismiss();
              this.toastMessage("1155");
              this.router.navigate(["/partial-registration"], { queryParams: this.params });
              // Failure tracking: status not success or token missing
              this.GTMSegment.collectVisitorData("", "", 1, 0);
            }
          },
          error: (err) => {
            if (!this.active) return;
            this.loadingService.dismiss();
            this.GTMSegment.collectVisitorData("", "", 1, 0);
            // Check if this is an actual network connectivity issue
            Network.getStatus().then(status => {
              if (!this.active) return;
              if (!status.connected) {
                // Real network offline - redirect to offline page
                this.timeoutManager.clearTimeout();
                this.router.navigate(['/offline-waiting'], { queryParams: this.params });
              } else if (err?.message === 'OFFLINE_ERROR') {
                // Legacy offline error handling
                this.timeoutManager.clearTimeout();
                this.router.navigate(['/offline-waiting'], { queryParams: this.params });
              } else {
                this.showLoginRecovery();
              }
            }).catch(() => this.showLoginRecovery());
            
            return err;
          },
        });
    } catch (error) {
      if (!this.active) return;
      console.error('Error in checkCustomLogin:', error);
      this.loadingService.dismiss();
      
      // Check if this is a private browsing/IndexedDB error
      const errorMessage = (error as Error)?.message?.toLowerCase() || '';
      const isPrivateBrowsingError = 
        errorMessage.includes('undefined is not an object') && 
        errorMessage.includes('createobjectstore');
      
      if (isPrivateBrowsingError) {
        console.warn('Private browsing IndexedDB error caught. Proceeding with redirect.');
      }
      
      this.showLoginRecovery();
    }
  }

  private showLoginRecovery() {
    if (!this.active) return;
    this.active = false;
    this.loginFailed = true;
    this.timeoutManager.clearTimeout();
    this.destroyCallAuth$.next();
    this.loadingService.dismiss();
  }

  retryLogin() {
    window.location.reload();
  }

  /**
   * Determines if an error is a transient Firestore SDK offline error
   * (false positive) versus an actual network connectivity issue.
   */
  private isTransientFirestoreError(error: any): boolean {
    if (!error) return false;

    const errorMessage = error?.message?.toLowerCase() || '';
    const errorString = String(error).toLowerCase();

    // Check for Firestore "client is offline" errors
    const isFirestoreOffline = 
      errorMessage.includes('client is offline') ||
      errorMessage.includes('failed to get document') ||
      errorString.includes('client is offline') ||
      errorString.includes('failed to get document');

    if (!isFirestoreOffline) {
      return false; // Not a Firestore offline error
    }

    // This is a Firestore offline error - it's transient if we actually have network
    // The retry mechanism will give the SDK time to recover
    return true;
  }

  ngOnDestroy() {
    this.active = false;
    this.timeoutManager.clearTimeout();
    this.destroyCallAuth$.next();
    this.destroyCallAuth$.complete();
    this.loadingService.dismiss();
  }
  // Toast message if user tries to login too many times with wrong password
  toastMessage(toastCode: string) {
    const toastMap: Record<string, { message: string; icon: string }> = {
      "1154": { message: "Auto logged in", icon: "tick circle success.png" },
      "1155": { message: "Auto login failed", icon: "tick circle error.svg" },
    };
    const defaultData = toastMap[toastCode] || { message: "", icon: "tick circle error.svg" };
    const defaultMsg = {
      message: {
        line_1: defaultData.message,
        line_2: "",
      },
      style: {
        icon: defaultData.icon,
        type: "error",
      },
    };
  
    this.authService.getConstantsMsg(toastCode)
      .then((translatedMsg) => this.authService.showToastMessage(translatedMsg, defaultMsg))
      .catch((error) => error);
  }
  
}

interface AuthParams {
  t?: string;
  T?: string;
  [key: string]: unknown;
}
